import { X } from 'lucide-react'
import { Card } from '@/components/ui'
import { formatCurrency } from '@/lib/utils'

export interface CrmComparisonRow {
  date: string
  appRevenue: number
  crmRevenue: number
  crmSubscriptionRevenue: number
  hasCrmData: boolean
  delta: number
}

export interface CrmTotalsViewModel {
  appRevenue: number
  crmRevenue: number | null
  delta: number | null
  deltaPercent: number | null
  crmSubscriptionRevenue: number | null
  dailyRows: CrmComparisonRow[]
}

export interface CrmPaymentChannel {
  name: string
  amount: number
  share: number
}

interface CrmComparisonModalProps {
  isOpen: boolean
  rangeLabel: string
  totals: CrmTotalsViewModel
  paymentChannels: CrmPaymentChannel[]
  coverage?: {
    totalStudios: number
    studiosWithCrmData: number
    hasPartialCoverage: boolean
  }
  loading: boolean
  error: string | null
  lastSyncAt: string | null
  onClose: () => void
  currency?: string
  showSubscriptionRevenue?: boolean
}

export function CrmComparisonModal({
  isOpen,
  rangeLabel,
  totals,
  paymentChannels,
  coverage,
  loading,
  error,
  lastSyncAt,
  onClose,
  currency = 'RUB',
  showSubscriptionRevenue = false
}: CrmComparisonModalProps) {
  if (!isOpen) return null

  const displayedDelta = totals.delta ?? totals.appRevenue

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-card rounded-xl border border-border max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-base font-medium">Сверка выручки с Listok CRM</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-muted transition-colors"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-auto max-h-[calc(85vh-60px)] p-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <h4 className="text-sm sm:text-base font-medium mb-3">Сверка с Listok CRM</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Выручка в приложении</span>
                  <span className="font-semibold">{formatCurrency(totals.appRevenue, currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Выручка в Listok CRM</span>
                  <span className="font-semibold">
                    {formatCurrency(totals.crmRevenue ?? 0, currency)}
                  </span>
                </div>
                {showSubscriptionRevenue && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Выручка подписок в Listok</span>
                    <span className="font-semibold">
                      {formatCurrency(totals.crmSubscriptionRevenue ?? 0, currency)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Расхождение</span>
                  <div className={`text-right ${
                    displayedDelta === 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    <div className="font-semibold">
                      {`${displayedDelta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(displayedDelta), currency)}`}
                    </div>
                    {totals.delta !== null && totals.deltaPercent !== null && (
                      <div className="text-xs">
                        ({totals.deltaPercent >= 0 ? '+' : ''}{totals.deltaPercent.toFixed(2)}%)
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Дата последней синхронизации</span>
                  <span className="font-semibold">
                    {lastSyncAt ? new Date(lastSyncAt).toLocaleString('ru-RU') : 'Нет данных'}
                  </span>
                </div>
                {coverage && coverage.totalStudios > 1 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Покрытие CRM по студиям</span>
                    <span className={`font-semibold ${coverage.hasPartialCoverage ? 'text-amber-700' : 'text-foreground'}`}>
                      {coverage.studiosWithCrmData}/{coverage.totalStudios}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-border text-xs">
                {loading && <span className="text-muted-foreground">Загрузка данных Listok CRM...</span>}
                {!loading && error && <span className="text-red-700">{error}</span>}
              </div>
            </Card>
            <Card>
              <h4 className="text-sm sm:text-base font-medium mb-3">Каналы оплаты Listok CRM</h4>
              {paymentChannels.length === 0 ? (
                <p className="text-sm text-muted-foreground">Нет данных по каналам оплаты за выбранный диапазон.</p>
              ) : (
                <div className="space-y-2">
                  {paymentChannels.map((channel) => (
                    <div key={channel.name} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{channel.name}</span>
                        <div className="text-right">
                          <div className="font-semibold">{formatCurrency(channel.amount, currency)}</div>
                          <div className="text-xs text-muted-foreground">({channel.share.toFixed(0)}%)</div>
                        </div>
                      </div>
                      <div className="h-2 rounded bg-muted overflow-hidden">
                        <div
                          className="h-full bg-primary rounded"
                          style={{ width: `${Math.max(2, Math.min(channel.share, 100))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <h4 className="text-sm sm:text-base font-medium">Детализация по дням</h4>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr>
                <th className="text-left p-3">День</th>
                <th className="text-right p-3">Выручка в приложении</th>
                <th className="text-right p-3">Выручка в Listok CRM</th>
                {showSubscriptionRevenue && (
                  <th className="text-right p-3">Подписки Listok CRM</th>
                )}
                <th className="text-right p-3">Δ</th>
              </tr>
            </thead>
            <tbody>
              {totals.dailyRows.map((row) => (
                <tr key={row.date} className="border-b border-border/50">
                  <td className="p-3">{new Date(row.date).toLocaleDateString('ru-RU')}</td>
                  <td className="p-3 text-right">{formatCurrency(row.appRevenue, currency)}</td>
                  <td className="p-3 text-right">{formatCurrency(row.hasCrmData ? row.crmRevenue : 0, currency)}</td>
                  {showSubscriptionRevenue && (
                    <td className="p-3 text-right">
                      {formatCurrency(row.hasCrmData ? row.crmSubscriptionRevenue : 0, currency)}
                    </td>
                  )}
                  <td className={`p-3 text-right font-semibold ${!row.hasCrmData ? 'text-muted-foreground' : row.delta === 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {`${row.delta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(row.delta), currency)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
